'use client';

import { useMemo } from 'react';
import { Spinner } from '@phosphor-icons/react';
import { DropRippleLoader } from './drop-ripple-loader';
import { getStreamingHtmlPreview, type StreamingHtmlStage } from '@/lib/html-streaming';
import { useThrottledValue } from '@/lib/use-throttled-value';
import { cn } from '@/lib/utils';

interface StreamingHtmlPreviewProps {
  input?: Record<string, unknown>;
  className?: string;
}

// Below this size the preview would just be a flash of blank/partial content — show the loader instead.
const PREVIEW_CHAR_THRESHOLD = 240;

// Iframe srcDoc updates trigger a full reload — flashes the content and
// resets scroll. Cap re-renders to one per this window during streaming so
// the user can actually scroll a streaming preview without being thrown
// back to the top every chunk.
const SRCDOC_THROTTLE_MS = 700;

const STAGE_LABEL: Record<StreamingHtmlStage, string> = {
  empty: 'Drafting layout…',
  styling: 'Designing styles…',
  building: 'Building layout…',
  interactive: 'Adding interactivity…',
};

const STAGE_HEADER: Record<StreamingHtmlStage, string> = {
  empty: 'Preparing HTML',
  styling: 'Streaming styles',
  building: 'Streaming HTML',
  interactive: 'Streaming interactivity',
};

export function StreamingHtmlPreview({ input, className }: StreamingHtmlPreviewProps) {
  const preview = getStreamingHtmlPreview(input);
  const shouldShowPreview = !!preview && preview.contentLength >= PREVIEW_CHAR_THRESHOLD && !!preview.html;

  const srcDoc = useMemo(() => {
    if (!shouldShowPreview || !preview) return '';
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>
  *, *::before, *::after { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 12px; width: 100%;
    background: transparent; color: #111827;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }
  img, svg, canvas, iframe, video { max-width: 100%; }
  ${preview.css || ''}
</style>
</head>
<body>
${preview.html}
${preview.js && preview.js.trim() ? `<script>${preview.js}</script>` : ''}
</body>
</html>`;
  }, [shouldShowPreview, preview]);

  const minHeight = preview?.minHeight ?? 260;
  const maxHeight = preview?.maxHeight;
  const stage: StreamingHtmlStage = preview?.stage ?? 'empty';

  // Throttle the srcDoc the iframe receives so streaming updates don't
  // reload on every chunk. User-visible effect: scroll position inside the
  // iframe is preserved between bursts.
  const stableSrcDoc = useThrottledValue(srcDoc, SRCDOC_THROTTLE_MS);

  return (
    <div className={cn('flex flex-col rounded-xl border border-border bg-muted/20', className)}>
      <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
        <Spinner className="size-3.5 animate-spin" weight="bold" />
        <span>{STAGE_HEADER[stage]}</span>
        {preview?.title ? (
          <span className="min-w-0 truncate text-foreground/80">· {preview.title}</span>
        ) : null}
      </div>

      <div
        className="flex flex-1 flex-col overflow-hidden rounded-b-xl"
        style={{ minHeight, maxHeight }}
      >
        {shouldShowPreview ? (
          <iframe
            srcDoc={stableSrcDoc}
            title={preview?.title ?? 'HTML preview'}
            className="block w-full flex-1 border-0"
            style={{ minHeight, maxHeight }}
            sandbox="allow-scripts"
          />
        ) : (
          <DropRippleLoader label={STAGE_LABEL[stage]} />
        )}
      </div>
    </div>
  );
}
